import type { DraftAnswers, DraftChoice } from './types.ts';

export type ScriptDurationTarget = {
  label: string;
  seconds: number;
  minWords: number;
  maxWords: number;
};

const minWordsPerMinute = 125;
const maxWordsPerMinute = 165;
const numberWords: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

export function getScriptDurationTarget(choices: DraftChoice[], answers: DraftAnswers): ScriptDurationTarget | null {
  const choice = choices.find((item) => item.id === 'script_length');
  const answer = choice ? answers[choice.id] : undefined;
  if (!choice || typeof answer !== 'string') return null;

  const option = choice.options.find((item) => item.value === answer);
  const source = `${option?.label ?? ''} ${answer}`;
  const seconds = parseDurationSeconds(source);
  if (!seconds) return null;

  const minutes = seconds / 60;
  return {
    label: option?.label.replace(/\s*\(recommended\)\s*/i, '').trim() || `${Math.round(minutes)} minutes`,
    seconds,
    minWords: Math.max(80, Math.round(minutes * minWordsPerMinute)),
    maxWords: Math.max(120, Math.round(minutes * maxWordsPerMinute)),
  };
}

export function countSpokenWords(script: string) {
  const lines = script.split(/\r?\n/);
  const scriptStart = lines.findIndex((line) => /^\s*SCRIPT\s*:/i.test(line));
  const spokenLines = (scriptStart >= 0 ? lines.slice(scriptStart + 1) : lines).filter((line) => {
    const trimmed = line.trim();
    return trimmed && !/^\s*(FORMAT|RUNTIME|CAST)\s*:/i.test(trimmed) && !/^\s*\[.*\]\s*$/.test(trimmed);
  });

  const spokenText = spokenLines
    .join(' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/^[A-Z][A-Z0-9 _-]{0,40}:\s*/gm, ' ');
  return spokenText.match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu)?.length ?? 0;
}

export function isScriptLengthWithinTarget(script: string, target: ScriptDurationTarget) {
  const wordCount = countSpokenWords(script);
  return wordCount >= target.minWords && wordCount <= target.maxWords;
}

function parseDurationSeconds(source: string) {
  const normalized = source.toLowerCase().replace(/[_-]+/g, ' ');
  const numericMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(seconds?|secs?|minutes?|mins?)/i);
  if (numericMatch) {
    return toSeconds(Number(numericMatch[1]), numericMatch[2]);
  }

  const wordMatch = normalized.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten)\s*(seconds?|secs?|minutes?|mins?)\b/i);
  if (wordMatch) {
    return toSeconds(numberWords[wordMatch[1].toLowerCase()], wordMatch[2]);
  }

  return null;
}

function toSeconds(value: number, unit: string) {
  return /sec/i.test(unit) ? Math.round(value) : Math.round(value * 60);
}
